/**
 * The board's filter tabs, and the pure predicates behind them.
 *
 * The technician home used to be one stacked list — your work, then the
 * open pool, then colleagues', then today's receipts — with the topbar
 * carrying nav to Inventory and Services. Those two moved out and the
 * board's own slices moved in: the topbar is a filter bar now, not
 * navigation.
 *
 * TWO feeds back this, because no single endpoint has it all:
 *
 *   - `/technical/do-list` — the WO → service tree the cards render
 *     from. Scoped to now: your open/pending work, the loose pool, and
 *     YOUR work finished TODAY. Nothing older, nothing scheduled ahead.
 *   - `/technical/work-orders/active` — every WO assigned to you,
 *     `openedAt DESC`, with no status filter despite the name. This is
 *     where "done, ever" and "scheduled later" come from.
 *
 * So DONE and UPCOMING read flat `WorkOrderResponse` rows and render a
 * leaner card; the other three read the tree. That split is the price of
 * doing this without a backend change, and it is confined to here.
 */
import type { DoListWorkOrder, WorkOrderResponse } from '@/apis/types'
import { hasMyPaused, hasMyRunning, isClaimable, isDoneToday, isMine } from './todayGroups'

/**
 * Tabs, in bar order. `board` is the default and is the screen exactly as
 * it was — the other four are slices of it, so landing on one of them by
 * default would hide work a technician expects to see on arrival.
 */
export const BOARD_FILTERS = ['board', 'undispatched', 'progress', 'upcoming', 'done'] as const

export type BoardFilter = (typeof BOARD_FILTERS)[number]

export const DEFAULT_FILTER: BoardFilter = 'board'

/** The `?filter=` value, defaulted and validated — an unknown one (an old
 *  link, a typo) falls back rather than rendering an empty board. */
export function parseFilter(raw: string | null): BoardFilter {
  return BOARD_FILTERS.includes(raw as BoardFilter) ? (raw as BoardFilter) : DEFAULT_FILTER
}

/** Which feed a tab reads. Drives what the route fetches and renders. */
export function feedFor(filter: BoardFilter): 'tree' | 'flat' {
  return filter === 'done' || filter === 'upcoming' ? 'flat' : 'tree'
}

/**
 * DONE is capped rather than paged. `/active` has no paging, and a
 * technician two years in would otherwise pull every job they have ever
 * touched on each visit. Twenty is what fits a glance at "what did I
 * just finish".
 */
export const DONE_LIMIT = 20

// ── tree-fed tabs ──────────────────────────────────────────────────────

/** Nobody has taken it — the floor pool, there to be claimed. Once the
 *  signed-in technician already holds a stake in a job, its remaining loose
 *  work moved to the per-service Take, so `isClaimable` drops it from here
 *  too (see `todayGroups.ts`). */
export function undispatched(
  workOrders: readonly DoListWorkOrder[],
  meId: string | null = null,
): readonly DoListWorkOrder[] {
  return workOrders.filter((wo) => isClaimable(wo, meId))
}

/**
 * Work I have in hand — running OR paused.
 *
 * Paused belongs here: the tech stopped the clock to fetch a part or take
 * a call, and the job is still theirs and still unfinished. Leaving it out
 * would make a job vanish from the one tab that answers "what am I on"
 * at the exact moment they stepped away from it.
 *
 * Not "the job is IN_PROGRESS" — a colleague's running task makes the job
 * that too, and this tab is about what THIS technician is holding.
 */
export function inProgress(
  workOrders: readonly DoListWorkOrder[],
): readonly DoListWorkOrder[] {
  return workOrders.filter((wo) => hasMyRunning(wo) || hasMyPaused(wo))
}

// ── flat-fed tabs ──────────────────────────────────────────────────────

/**
 * Finished, newest first, capped.
 *
 * COMPLETED is the manager's sign-off. FINISHED — the technician has put
 * their tools down but the job is still awaiting review — is deliberately
 * NOT here: it is still live work, and a technician looking for what they
 * finished is asking about jobs that are closed, not ones waiting on
 * somebody else.
 */
export function done(workOrders: readonly WorkOrderResponse[]): readonly WorkOrderResponse[] {
  return workOrders
    .filter((wo) => wo.status === 'COMPLETED')
    .slice()
    .sort(byRecency)
    .slice(0, DONE_LIMIT)
}

/**
 * Assigned to me and booked for later — soonest first, because the next
 * one is the one being asked about.
 *
 * A job with no `scheduledAt` is not upcoming: a walk-in has no slot and
 * inventing one would put every unbooked job on the list. Cancelled and
 * completed jobs are excluded whatever their time says — a cancelled
 * booking in the future is not work.
 */
export function upcoming(
  workOrders: readonly WorkOrderResponse[],
  now: Date = new Date(),
): readonly WorkOrderResponse[] {
  const from = now.getTime()
  return workOrders
    .filter((wo) => {
      if (wo.status === 'COMPLETED' || wo.status === 'CANCELLED') return false
      const at = wo.scheduledAt ? Date.parse(wo.scheduledAt) : NaN
      return Number.isFinite(at) && at > from
    })
    .slice()
    .sort((a, b) => Date.parse(a.scheduledAt!) - Date.parse(b.scheduledAt!))
}

/** Newest first, on the best timestamp a row actually carries. */
function byRecency(a: WorkOrderResponse, b: WorkOrderResponse): number {
  return recencyOf(b) - recencyOf(a)
}

/**
 * `completedAt` is the honest "when did this finish", but it is optional
 * on the wire, so `openedAt` then `updatedAt` stand in. Without the
 * fallbacks a row missing one timestamp sorts as epoch zero and pins
 * itself to the bottom forever.
 */
function recencyOf(wo: WorkOrderResponse): number {
  for (const stamp of [wo.completedAt, wo.openedAt, wo.updatedAt]) {
    const t = stamp ? Date.parse(stamp) : NaN
    if (Number.isFinite(t)) return t
  }
  return 0
}

/** Re-exported so the route imports its predicates from one place. */
export { isMine, isDoneToday }
